import { writable, get } from "svelte/store";

export const questionTypes = [
  "Text",
  "CheckBox",
  "Select",
  "Quant",
  "Map",
  "DateTime",
];

// Helper function to check if a question is a self node
export function isSelfNodeQuestion(question) {
  if (!question.dataset || !Array.isArray(question.dataset)) return false;
  return question.dataset.some((handler) => handler[0] === "setSelf");
}

// Helper function to get relationship type from dataset
export function getRelationTypeFromDataset(dataset, question = null) {
  if (!dataset || !Array.isArray(dataset)) return "unknown";

  if (dataset.some((item) => item[0] === "setSelf")) return "selfNode";
  if (dataset.some((item) => item[0] === "nodes")) return "createNodes";
  if (dataset.some((item) => item[0] === "nodeProps")) return "addProps";
  if (
    dataset.some(
      (item) =>
        item[0] === "selfProps" && !dataset.some((i) => i[0] === "setSelf")
    )
  )
    return "selfProps";

  return "unknown";
}

// Create the store
function createQuestionsetStore() {
  const { subscribe, set, update } = writable({
    labelOrganisation: "",
    labelProject: "",
    sections: {},
    questions: [],
    map: undefined,
    config: {
      projectFlag: "sm",
      publicProject: true,
      published: false,
      updateable: true,
      multiAnswers: false,
      multiRings: false,
      mapOn: false,
      timeline: false,
      tour: false,
      explore: false,
      workspace: false,
      filterActions: [],
      filterBoxes: [],
      lenseActions: [],
      filterBoxTitles: [],
      linkActions: [],
      translations: {},
      extraMarkup: "",
      delRel: [],
    },
  });

  return {
    subscribe,
    set,
    update,

    // Ensure questions have proper sequential IDs
    normalizeQuestionIds(questionsArray) {
      return questionsArray.map((question, index) => ({
        ...question,
        id: `question--${index}`,
      }));
    },

    // Validate and update a question, ensuring selfNode rules
    updateQuestion(index, updatedQuestion) {
      update((data) => {
        const newQuestions = [...data.questions];
        const oldQuestion = newQuestions[index];

        const wasOldSelfNode = isSelfNodeQuestion(oldQuestion);
        const isNewSelfNode = isSelfNodeQuestion(updatedQuestion);

        // If we're making this question a selfNode
        if (!wasOldSelfNode && isNewSelfNode) {
          // Check if there's already a selfNode
          const existingSelfNodeIndex = newQuestions.findIndex(
            (q, idx) => idx !== index && isSelfNodeQuestion(q)
          );

          if (existingSelfNodeIndex !== -1) {
            throw new Error(
              `Cannot create multiple self nodes. Question at index ${existingSelfNodeIndex} is already a self node.`
            );
          }
        }

        // Ensure proper ID
        updatedQuestion.id = `question--${index}`;

        // Update the question
        newQuestions[index] = updatedQuestion;

        return {
          ...data,
          questions: newQuestions,
        };
      });
    },

    // Add a new question with selfNode validation and proper ID
    addQuestion(newQuestion) {
      update((data) => {
        const isNewSelfNode = isSelfNodeQuestion(newQuestion);

        // If we're adding a selfNode, check if one already exists
        if (isNewSelfNode) {
          const existingSelfNodeIndex = data.questions.findIndex((q) =>
            isSelfNodeQuestion(q)
          );

          if (existingSelfNodeIndex !== -1) {
            throw new Error(
              `Cannot create multiple self nodes. Question at index ${existingSelfNodeIndex} is already a self node.`
            );
          }
        }

        // Assign proper ID based on position
        const newIndex = data.questions.length;
        newQuestion.id = `question--${newIndex}`;

        return {
          ...data,
          questions: [...data.questions, newQuestion],
        };
      });
    },

    // Remove a question and resequence IDs
    removeQuestion(index) {
      update((data) => {
        const newQuestions = [...data.questions];
        newQuestions.splice(index, 1);

        // Resequence all IDs
        const resequencedQuestions = this.normalizeQuestionIds(newQuestions);

        return {
          ...data,
          questions: resequencedQuestions,
        };
      });
    },

    // Get information about the current selfNode
    getSelfNodeInfo() {
      const data = get({ subscribe });
      const selfNodeIndex = data.questions.findIndex((q) =>
        isSelfNodeQuestion(q)
      );

      if (selfNodeIndex === -1) {
        return { exists: false, index: -1, question: null };
      }

      return {
        exists: true,
        index: selfNodeIndex,
        question: data.questions[selfNodeIndex],
      };
    },

    // Convert a question to selfNode (removing any existing selfNode)
    convertToSelfNode(index, confirmCallback = null) {
      const data = get({ subscribe });
      const targetQuestion = data.questions[index];

      if (isSelfNodeQuestion(targetQuestion)) {
        // Already a selfNode, nothing to do
        return;
      }

      const existingSelfNodeIndex = data.questions.findIndex((q) =>
        isSelfNodeQuestion(q)
      );

      if (existingSelfNodeIndex !== -1) {
        // There's already a selfNode - ask for confirmation
        if (confirmCallback) {
          const confirmed = confirmCallback(existingSelfNodeIndex);
          if (!confirmed) return;
        } else {
          const confirmed = confirm(
            `Question ${existingSelfNodeIndex} is currently the self node. Replace it with question ${index}?`
          );
          if (!confirmed) return;
        }
      }

      update((data) => {
        const newQuestions = [...data.questions];

        // Remove selfNode status from existing selfNode
        if (existingSelfNodeIndex !== -1) {
          const oldSelfNode = { ...newQuestions[existingSelfNodeIndex] };
          // Convert back to regular node creation
          oldSelfNode.dataset = [
            ["nodes", { type: "node-type" }],
            [
              "links",
              [
                {
                  source: "__self__",
                  target: "__answers__",
                  type: "link-type",
                },
              ],
            ],
          ];
          newQuestions[existingSelfNodeIndex] = oldSelfNode;
        }

        // Convert target question to selfNode
        const newSelfNode = { ...newQuestions[index] };
        newSelfNode.dataset = [
          ["setSelf"],
          ["selfProps", { type: "organization", survey: "started" }],
        ];
        newQuestions[index] = newSelfNode;

        return {
          ...data,
          questions: newQuestions,
        };
      });
    },

    // Remove selfNode status from a question
    removeSelfNodeStatus(index, confirmCallback = null) {
      const data = get({ subscribe });
      const targetQuestion = data.questions[index];

      if (!isSelfNodeQuestion(targetQuestion)) {
        // Not a selfNode, nothing to do
        return;
      }

      if (confirmCallback) {
        const confirmed = confirmCallback();
        if (!confirmed) return;
      } else {
        const confirmed = confirm(
          "Remove self node status? This will convert it to a regular node creation question."
        );
        if (!confirmed) return;
      }

      update((data) => {
        const newQuestions = [...data.questions];

        // Convert selfNode back to regular node creation
        const updatedQuestion = { ...newQuestions[index] };
        updatedQuestion.dataset = [
          ["nodes", { type: "node-type" }],
          [
            "links",
            [{ source: "__self__", target: "__answers__", type: "link-type" }],
          ],
        ];
        newQuestions[index] = updatedQuestion;

        return {
          ...data,
          questions: newQuestions,
        };
      });
    },

    // Section management
    updateSection(sectionId, title, text) {
      update((data) => ({
        ...data,
        sections: {
          ...data.sections,
          [sectionId]: { title, text },
        },
      }));
    },

    removeSection(sectionId) {
      update((data) => {
        const { [sectionId]: removed, ...remainingSections } = data.sections;
        return {
          ...data,
          sections: remainingSections,
        };
      });
    },

    // Import JSON data with ID normalization
    importJson(jsonData) {
      // Normalize questions to ensure proper IDs
      const normalizedQuestions = jsonData.questions
        ? this.normalizeQuestionIds(jsonData.questions)
        : [];

      set({
        labelOrganisation: jsonData.labelOrganisation || "",
        labelProject: jsonData.labelProject || "",
        sections: jsonData.sections || {},
        questions: normalizedQuestions,
        map: jsonData.map || undefined,
        config: {
          projectFlag: "sm",
          publicProject: true,
          published: false,
          updateable: true,
          multiAnswers: false,
          multiRings: false,
          mapOn: false,
          timeline: false,
          tour: false,
          explore: false,
          workspace: false,
          filterActions: [],
          filterBoxes: [],
          lenseActions: [],
          filterBoxTitles: [],
          linkActions: [],
          translations: {},
          extraMarkup: "",
          delRel: [],
          ...jsonData.config,
        },
      });
    },

    // Export JSON data
    exportJson() {
      const data = get({ subscribe });
      const result = {
        labelOrganisation: data.labelOrganisation,
        labelProject: data.labelProject,
        sections: data.sections,
        questions: data.questions,
        config: data.config,
      };

      // Only include map if it exists
      if (data.map) {
        result.map = data.map;
      }

      return result;
    },
  };
}

export const questionsetStore = createQuestionsetStore();
